// Data Migration Service for TrackProfit
// Handles IndexedDB schema updates and data migration

import { db } from '../db';
import { dataService } from './dataService';

const MIGRATION_VERSION = '1.0.0';
const STORAGE_KEY = 'trackprofit_migration_version';

class MigrationService {
  constructor() {
    this.migrations = [
      {
        version: '1.0.0',
        description: 'Initial offline-first implementation',
        migrate: this.migrateToV1_0_0.bind(this)
      }
    ];
  }

  // Check if migration is needed
  async needsMigration() {
    try {
      const currentVersion = localStorage.getItem(STORAGE_KEY);
      const latestVersion = this.migrations[this.migrations.length - 1].version;
      
      if (!currentVersion) {
        return { needed: true, from: null, to: latestVersion };
      }
      
      if (currentVersion !== latestVersion) {
        return { needed: true, from: currentVersion, to: latestVersion };
      }
      
      return { needed: false, from: currentVersion, to: latestVersion };
    } catch (error) {
      console.error('Failed to check migration status:', error);
      return { needed: false, from: null, to: null };
    }
  }

  // Run all pending migrations
  async runMigrations() {
    try {
      const { needed, from, to } = await this.needsMigration();
      
      if (!needed) {
        console.log('No migrations needed');
        return { success: true, message: 'No migrations needed' };
      }

      console.log(`Running migrations from ${from || 'initial'} to ${to}`);
      
      const migrationsToRun = this.migrations.filter(migration => {
        if (!from) return true; // Run all if no previous version
        return this.compareVersions(migration.version, from) > 0;
      });

      for (const migration of migrationsToRun) {
        console.log(`Running migration ${migration.version}: ${migration.description}`);
        await migration.migrate();
        console.log(`Migration ${migration.version} completed`);
      }

      // Update stored version
      localStorage.setItem(STORAGE_KEY, to);
      
      console.log('All migrations completed successfully');
      return { success: true, message: 'Migrations completed successfully' };
    } catch (error) {
      console.error('Migration failed:', error);
      return { success: false, error: error.message };
    }
  }

  // Compare version strings (simple semantic versioning)
  compareVersions(version1, version2) {
    const v1Parts = version1.split('.').map(Number);
    const v2Parts = version2.split('.').map(Number);
    
    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const v1Part = v1Parts[i] || 0;
      const v2Part = v2Parts[i] || 0;
      
      if (v1Part > v2Part) return 1;
      if (v1Part < v2Part) return -1;
    }
    
    return 0;
  }

  // Migration to v1.0.0 - Initial offline-first implementation
  async migrateToV1_0_0() {
    try {
      console.log('Migrating to v1.0.0: Setting up offline-first infrastructure');
      
      // Initialize IndexedDB with proper schema
      await db.open();
      
      // Set up initial metadata
      await dataService.setMetadata('migration_completed', {
        version: '1.0.0',
        timestamp: Date.now(),
        description: 'Initial offline-first implementation'
      });
      
      // Set up sync status metadata
      await dataService.setMetadata('lastSync', null);
      await dataService.setMetadata('syncEnabled', true);
      
      console.log('v1.0.0 migration completed');
    } catch (error) {
      console.error('v1.0.0 migration failed:', error);
      throw error;
    }
  }

  // Get migration status
  async getStatus() {
    try {
      const currentVersion = localStorage.getItem(STORAGE_KEY);
      const { needed, from, to } = await this.needsMigration();
      
      return {
        currentVersion,
        needsMigration: needed,
        fromVersion: from,
        toVersion: to,
        availableMigrations: this.migrations.length
      };
    } catch (error) {
      console.error('Failed to get migration status:', error);
      return {
        currentVersion: null,
        needsMigration: false,
        fromVersion: null,
        toVersion: null,
        availableMigrations: 0,
        error: error.message
      };
    }
  }

  // Reset migration status (for development/testing)
  async resetMigrationStatus() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      console.log('Migration status reset');
      return { success: true };
    } catch (error) {
      console.error('Failed to reset migration status:', error);
      return { success: false, error: error.message };
    }
  }

  // Get migration history
  async getMigrationHistory() {
    try {
      const history = [];
      
      // Check for migration metadata in IndexedDB
      const migrationData = await dataService.getMetadata('migration_completed');
      if (migrationData) {
        history.push({
          version: migrationData.value.version,
          timestamp: migrationData.value.timestamp,
          description: migrationData.value.description
        });
      }
      
      return history;
    } catch (error) {
      console.error('Failed to get migration history:', error);
      return [];
    }
  }
}

// Create singleton instance
const migrationService = new MigrationService();

export default migrationService;



